<template>
  <ui-side-modal v-show="show"
                 class="banner-editor-modal"
                 :width="980"
                 @close="handleClose">
    <div class="banner-editor">
      <div class="editor-top">
        <h4 class="title" v-if="currentMode === 'create'">배너 생성</h4>
        <div class="actions">
          <ui-button :color="'blue'"
                     @click="previewCurrent">
            <i class="xi-eye"></i> 미리보기
          </ui-button>
          <template v-if="isDetailMode">
            <ui-button :color="'gray'"
                       @click="removeBannerItem">
              <i class="xi-trash"></i> 삭제
            </ui-button>
            <ui-button :color="'yellow'"
                       @click="startEdit">
              <i class="xi-pen"></i> 수정
            </ui-button>
          </template>
          <template v-else>
            <ui-button :color="'gray'"
                       @click="cancelEditOrCreate">
              <i class="xi-close"></i> 취소
            </ui-button>
            <ui-button :color="'pink'"
                       @click="save">
              <i class="xi-check"></i> 저장
            </ui-button>
          </template>
        </div>
      </div>

      <div class="editor-content">
        <section class="section">
          <h5>배너 정보</h5>
          <div class="grid">
            <div v-if="form._id"
                 :class="['row', { 'readonly-edit-row': currentMode === 'edit' }]">
              <label>ID</label>
              <div class="value readonly">{{ form._id }}</div>
            </div>
            <div :class="['row', { 'readonly-edit-row': currentMode === 'edit' }]">
              <label>배너 종류</label>
              <div class="value">
                <div v-if="currentMode !== 'create'"
                     class="value readonly">
                  {{ form.bannerType || '-' }}
                </div>
                <select v-else
                        v-model="form.bannerType"
                        :disabled="!canEditBannerType">
                  <option value="">선택</option>
                  <option value="CX">CX</option>
                  <option value="GEO">GEO</option>
                  <option value="BLOG">BLOG</option>
                </select>
                <p v-if="errors.bannerType"
                   class="error">{{ errors.bannerType }}</p>
              </div>
            </div>
            <div class="row">
              <label>배너명</label>
              <div class="value">
                <div v-if="isDetailMode"
                     class="value readonly">
                  {{ form.bannerName || '-' }}
                </div>
                <input v-else
                       v-model="form.bannerName"
                       type="text"
                       placeholder="이름에서만 노출됩니다." />
                <p v-if="errors.bannerName"
                   class="error">{{ errors.bannerName }}</p>
              </div>
            </div>
            <div v-if="form.regDate"
                 :class="['row', { 'readonly-edit-row': currentMode === 'edit' }]">
              <label>등록일</label>
              <div class="value readonly">{{ formattedRegDate }}</div>
            </div>
            <div class="row">
              <label>상태</label>
              <div class="value">
                <div v-if="isDetailMode"
                     class="value readonly">
                  {{ form.status === 'public' ? '공개' : form.status === 'private' ? '비공개' : '-' }}
                </div>
                <select v-else
                        v-model="form.status">
                  <option value="public">공개</option>
                  <option value="private">비공개</option>
                </select>
                <p v-if="errors.status"
                   class="error">{{ errors.status }}</p>
              </div>
            </div>
            <div v-if="showSortOrder"
                 class="row">
              <label>노출 순서</label>
              <div class="value">
                <div v-if="isDetailMode"
                     class="value readonly">
                  {{ form.sortOrder || '-' }}
                </div>
                <select v-else
                        v-model.number="form.sortOrder">
                  <option value="">선택</option>
                  <option v-for="slot in sortSlots"
                          :key="`slot-${slot}`"
                          :value="slot">
                    {{ slot }}
                  </option>
                </select>
                <p v-if="errors.sortOrder"
                   class="error">{{ errors.sortOrder }}</p>
              </div>
            </div>
          </div>
        </section>

        <section class="section">
          <h5>배너 내용</h5>
          <div class="lang-tabs">
            <button v-for="lang in langs"
                    :key="lang"
                    :class="{ active: activeLang === lang }"
                    @click="activeLang = lang">
              {{ lang.toUpperCase() }}
            </button>
            <p class="lang-guide">모든 언어의 이미지와 연결 링크를 넣어야 저장됩니다.</p>
          </div>
          <p v-if="contentError"
             class="error content-error">{{ contentError }}</p>
          <div class="image-box">
            <img v-if="currentContent.imageUrl"
                 :src="currentContent.imageUrl"
                 alt="banner preview" />
            <div v-else
                 class="placeholder">
              클릭하여 이미지 업로드<br/>
              (최대 5MB)
            </div>
            <input :ref="`file-${activeLang}`"
                   type="file"
                   class="a11y-invisible"
                   accept="image/*"
                   @change="uploadImage($event, activeLang)" />
            <div v-if="!isDetailMode"
                 class="image-actions">
              <ui-button :color="'purple'"
                         @click="openFileDialog(activeLang)">
                업로드
              </ui-button>
              <ui-button v-if="currentContent.imageUrl"
                         :color="'gray'"
                         @click="removeImage(activeLang)">
                삭제
              </ui-button>
            </div>
          </div>

          <div class="grid">
            <div class="row">
              <label>Title</label>
              <div class="value">
                <div v-if="isDetailMode"
                     class="value readonly">
                  {{ currentContent.title || '-' }}
                </div>
                <input v-else
                       v-model="currentContent.title"
                       type="text"
                       placeholder="배너 타이틀을 입력하세요." />
                <p v-if="errors[`contents.${activeLang}.title`]"
                   class="error">{{ errors[`contents.${activeLang}.title`] }}</p>
              </div>
            </div>
            <div v-if="showContentLabel"
                 class="row">
              <label>Label</label>
              <div class="value">
                <div v-if="isDetailMode"
                     class="value readonly">
                  {{ currentContent.label || '-' }}
                </div>
                <input v-else
                       v-model="currentContent.label"
                       type="text"
                       placeholder="배너 라벨을 입력하세요." />
                <p v-if="errors[`contents.${activeLang}.label`]"
                   class="error">{{ errors[`contents.${activeLang}.label`] }}</p>
              </div>
            </div>
            <div class="row">
              <label>배너 내용</label>
              <div class="value">
                <div v-if="isDetailMode"
                     class="value readonly">
                  {{ currentContent.text || '-' }}
                </div>
                <input v-else
                       v-model="currentContent.text"
                       type="text"
                       placeholder="배너 텍스트가 업데이트됩니다." />
                <p v-if="errors[`contents.${activeLang}.text`]"
                   class="error">{{ errors[`contents.${activeLang}.text`] }}</p>
              </div>
            </div>
            <div class="row">
              <label>연결 링크</label>
              <div class="value">
                <div v-if="isDetailMode"
                     class="value readonly">
                  {{ currentContent.linkUrl || '-' }}
                </div>
                <input v-else
                       v-model="currentContent.linkUrl"
                       type="text"
                       placeholder="배너 클릭시 열리는 링크입니다." />
                <p v-if="errors[`contents.${activeLang}.linkUrl`]"
                   class="error">{{ errors[`contents.${activeLang}.linkUrl`] }}</p>
              </div>
            </div>
          </div>
        </section>

        <section class="section">
          <h5>기타</h5>
          <div class="grid">
            <div class="row">
              <label>메모</label>
              <div class="value">
                <div v-if="isDetailMode"
                     class="value readonly">
                  {{ form.memo || '-' }}
                </div>
                <input v-else
                       v-model="form.memo"
                       type="text" />
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </ui-side-modal>
</template>

<script>
import { format } from 'date-fns'
import {
  getBannerInfo,
  getBannerList,
  removeBanner,
  upsertBanner,
  uploadBannerImage
} from '@/api/marketing'
import { EventBus } from '@/utils/event-bus'
import { alertError } from '@/utils/tools'
import { isValidUrl } from '@/libs/validation-helper'

import UiButton from '@/components/_ui/UiButton'
import UiSideModal from '@/components/_ui/UiSideModal'
import {
  BANNER_CONTENT_LANGS,
  createBannerContent,
  normalizeBannerContent
} from '@/components/marketing/banner/bannerContentDefaults'

const createEmptyContents = (bannerType = '') => BANNER_CONTENT_LANGS.map((lang) => createBannerContent(lang, bannerType))

const createDefaultForm = (bannerType = '') => ({
  _id: null,
  bannerType,
  bannerName: '',
  status: 'private',
  sortOrder: '',
  memo: '',
  regDate: null,
  contents: createEmptyContents(bannerType)
})

const getPreviewSizeByBannerType = (bannerType) => {
  if (bannerType === 'BLOG') {
    return { width: 490, height: 224 }
  }
  return { width: 670, height: 102 }
}

export default {
  name: 'BannerEditorModal',
  components: {
    UiButton,
    UiSideModal
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'create'
    },
    bannerId: {
      type: String,
      default: null
    }
  },
  computed: {
    isDetailMode () {
      return this.currentMode === 'detail'
    },
    canEditBannerType () {
      return this.currentMode === 'create'
    },
    showSortOrder () {
      return this.form.bannerType === 'BLOG'
    },
    showContentLabel () {
      return this.form.bannerType === 'CX'
    },
    currentContent () {
      return this.getContentByLang(this.activeLang)
    },
    contentError () {
      const hasError = this.langs.some((lang) => {
        return this.errors[`contents.${lang}.imageUrl`] ||
          this.errors[`contents.${lang}.title`] ||
          this.errors[`contents.${lang}.label`] ||
          this.errors[`contents.${lang}.text`] ||
          this.errors[`contents.${lang}.linkUrl`]
      })
      return hasError ? '모든 언어의 이미지/Title/배너 내용/연결 링크는 필수입니다.' : ''
    },
    formattedRegDate () {
      if (!this.form.regDate) {
        return '-'
      }
      return format(new Date(this.form.regDate), 'yyyy-MM-dd HH:mm:ss')
    }
  },
  watch: {
    show (value) {
      if (value) {
        this.initialize()
      }
    },
    bannerId (value, oldValue) {
      if (this.show && this.mode !== 'create' && value && value !== oldValue) {
        this.initialize()
      }
    },
    mode (value) {
      this.currentMode = value
    },
    'form.bannerType' (value) {
      if (this.currentMode === 'create') {
        this.applyBannerContentDefaults(value)
      }
      if (value === 'BLOG') {
        this.loadSortSlots()
      } else {
        this.form.sortOrder = ''
      }
    }
  },
  methods: {
    getContentByLang (lang) {
      const found = this.form.contents.find((content) => content.lang === lang)
      if (found) {
        return found
      }
      const fallback = createBannerContent(lang, this.form.bannerType)
      this.form.contents.push(fallback)
      return fallback
    },
    applyBannerContentDefaults (bannerType) {
      this.form.contents = this.langs.map((lang) => {
        const current = this.getContentByLang(lang)
        const next = createBannerContent(lang, bannerType)
        return {
          ...current,
          title: next.title,
          label: next.label
        }
      })
    },
    async initialize () {
      this.activeLang = 'ko'
      this.errors = {}
      this.currentMode = this.mode
      this.originalForm = null
      if (this.mode === 'create') {
        this.form = createDefaultForm()
        this.sortSlots = [1, 2, 3]
        return
      }
      if (!this.bannerId) {
        return
      }
      EventBus.$emit('onLoading', true)
      try {
        const response = await getBannerInfo({ id: this.bannerId }, true)
        this.form = this.mapBannerInfo(response.result.info)
        this.originalForm = JSON.parse(JSON.stringify(this.form))
        await this.loadSortSlots()
      } catch (error) {
        alertError('배너 정보를 불러오지 못 했습니다.', error)
        this.$emit('close')
      } finally {
        EventBus.$emit('onLoading', false)
      }
    },
    mapBannerInfo (info) {
      const next = createDefaultForm()
      next._id = info._id || info.id || null
      next.bannerType = info.bannerType || ''
      next.bannerName = info.bannerName || ''
      next.status = info.status || 'private'
      next.sortOrder = typeof info.sortOrder === 'number' ? info.sortOrder : ''
      next.memo = info.memo || ''
      next.regDate = info.regDate || null
      const sourceContents = info.contents || {}
      if (Array.isArray(sourceContents)) {
        next.contents = this.langs.map((lang) => {
          const langData = sourceContents.find((item) => item.lang === lang) || {}
          return normalizeBannerContent({
            lang,
            imageUrl: langData.imageUrl || '',
            title: langData.title || '',
            label: langData.label || '',
            text: langData.text || '',
            linkUrl: langData.linkUrl || ''
          }, next.bannerType)
        })
      } else {
        next.contents = this.langs.map((lang) => {
          const langData = sourceContents[lang] || {}
          return normalizeBannerContent({
            lang,
            imageUrl: langData.imageUrl || '',
            title: langData.title || '',
            label: langData.label || '',
            text: langData.text || '',
            linkUrl: langData.linkUrl || ''
          }, next.bannerType)
        })
      }
      return next
    },
    async loadSortSlots () {
      if (this.form.bannerType !== 'BLOG') {
        this.sortSlots = [1, 2, 3]
        return
      }
      try {
        const response = await getBannerList({
          pageNum: 1,
          pageSize: 300,
          sortBy: 'sortOrder',
          searchFrom: {
            bannerType: 'BLOG',
            status: 'public'
          }
        }, true)
        const occupied = (response.result.list || [])
          .filter((item) => this.form._id ? item._id !== this.form._id : true)
          .map((item) => Number(item.sortOrder))
          .filter((value) => Number.isInteger(value) && value > 0)
        const maxOrder = Math.max(3, ...occupied, this.form.sortOrder || 0)
        const slots = []
        for (let i = 1; i <= maxOrder + 1; i++) {
          if (!occupied.includes(i) || i === this.form.sortOrder) {
            slots.push(i)
          }
        }
        this.sortSlots = slots.length ? slots : [1, 2, 3]
      } catch (error) {
        this.sortSlots = [1, 2, 3]
      }
    },
    openFileDialog (lang) {
      const refName = `file-${lang}`
      const fileInput = this.$refs[refName]
      if (fileInput && fileInput[0]) {
        fileInput[0].value = ''
        fileInput[0].click()
      } else if (fileInput) {
        fileInput.value = ''
        fileInput.click()
      }
    },
    async uploadImage (event, lang) {
      const file = event.target.files[0]
      if (!file) {
        return
      }
      if (!this.form.bannerType) {
        this.$set(this.errors, 'bannerType', '배너 종류를 먼저 선택하세요.')
        return
      }
      EventBus.$emit('onLoading', true)
      try {
        const response = await uploadBannerImage({
          image: file,
          bannerType: this.form.bannerType,
          lang
        })
        this.getContentByLang(lang).imageUrl = response.result.imageUrl || ''
        this.$delete(this.errors, `contents.${lang}.imageUrl`)
      } catch (error) {
        alertError('이미지 업로드에 실패했습니다.', error)
      } finally {
        EventBus.$emit('onLoading', false)
      }
    },
    removeImage (lang) {
      this.getContentByLang(lang).imageUrl = ''
    },
    validate () {
      this.errors = {}
      if (!this.form.bannerType) {
        this.$set(this.errors, 'bannerType', '배너 종류는 필수입니다.')
      }
      if (!this.form.bannerName) {
        this.$set(this.errors, 'bannerName', '배너명은 필수입니다.')
      }
      if (!this.form.status) {
        this.$set(this.errors, 'status', '상태는 필수입니다.')
      }
      if (this.form.bannerType === 'BLOG' && (this.form.sortOrder === '' || this.form.sortOrder === null)) {
        this.$set(this.errors, 'sortOrder', '노출 순서는 필수입니다.')
      }
      this.langs.forEach((lang) => {
        const content = this.getContentByLang(lang)
        if (!content.imageUrl) {
          this.$set(this.errors, `contents.${lang}.imageUrl`, '이미지는 필수입니다.')
        }
        if (!content.title) {
          this.$set(this.errors, `contents.${lang}.title`, 'Title은 필수입니다.')
        }
        if (this.form.bannerType === 'CX' && !content.label) {
          this.$set(this.errors, `contents.${lang}.label`, 'Label은 필수입니다.')
        }
        if (!content.text) {
          this.$set(this.errors, `contents.${lang}.text`, '배너 내용은 필수입니다.')
        }
        if (!content.linkUrl) {
          this.$set(this.errors, `contents.${lang}.linkUrl`, '연결 링크는 필수입니다.')
        } else if (!isValidUrl(content.linkUrl)) {
          this.$set(this.errors, `contents.${lang}.linkUrl`, 'http:// 또는 https:// 형식의 올바른 URL을 입력하세요.')
        }
      })
      return Object.keys(this.errors).length === 0
    },
    buildPayload () {
      return {
        _id: this.form._id || undefined,
        bannerType: this.form.bannerType,
        bannerName: this.form.bannerName,
        status: this.form.status,
        sortOrder: this.form.bannerType === 'BLOG' ? Number(this.form.sortOrder || 0) : 0,
        memo: this.form.memo || '',
        contents: this.form.contents.map((content) => ({
          lang: content.lang,
          imageUrl: content.imageUrl || '',
          title: content.title || '',
          label: this.form.bannerType === 'CX' ? content.label || '' : '',
          text: content.text || '',
          linkUrl: content.linkUrl || ''
        }))
      }
    },
    async save () {
      if (!this.validate()) {
        return
      }
      if (!confirm('정말로 저장하시겠습니까?')) {
        return
      }
      EventBus.$emit('onLoading', true)
      try {
        await upsertBanner(this.buildPayload(), true)
        this.$emit('saved')
      } catch (error) {
        const message = error.response ? error.response.data.message : null
        if (message && typeof message === 'object') {
          Object.entries(message).forEach(([key, value]) => {
            this.$set(this.errors, key, value)
          })
        } else {
          alertError('저장에 실패했습니다.', error)
        }
      } finally {
        EventBus.$emit('onLoading', false)
      }
    },
    async removeBannerItem () {
      if (!this.form._id) {
        return
      }
      if (!confirm('정말로 삭제하시겠습니까?')) {
        return
      }
      EventBus.$emit('onLoading', true)
      try {
        await removeBanner({ _id: this.form._id }, true)
        this.$emit('saved')
      } catch (error) {
        alertError('삭제에 실패했습니다.', error)
      } finally {
        EventBus.$emit('onLoading', false)
      }
    },
    previewCurrent () {
      const content = this.currentContent
      const previewSize = getPreviewSizeByBannerType(this.form.bannerType)
      const imageHtml = content.imageUrl
        ? `
          <div style="width:${previewSize.width}px;height:${previewSize.height}px;border:1px solid #e5e5e5;border-radius:8px;background-color:#fafafa;background-image:url('${content.imageUrl}');background-position:center;background-size:cover;background-repeat:no-repeat;overflow:hidden;"></div>
        `
        : `<div style="width:${previewSize.width}px;height:${previewSize.height}px;display:flex;align-items:center;justify-content:center;border:1px dashed #bdbdbd;border-radius:8px;background:#fafafa;color:#888;">이미지가 없습니다.</div>`
      const previewWindow = window.open('', '_blank', 'width=900,height=700')
      if (!previewWindow) {
        return
      }
      previewWindow.document.write(`
        <html>
          <head><title>Banner Preview</title></head>
          <body style="font-family:Arial,sans-serif;padding:24px;">
            <h3>${this.form.bannerName || '배너 미리보기'} (${this.activeLang.toUpperCase()})</h3>
            <p style="margin:0 0 8px;color:#666;">미리보기 크기: ${previewSize.width} x ${previewSize.height}</p>
            <div style="margin:16px 0;">${imageHtml}</div>
            ${content.label ? `<p style="margin:0 0 4px;color:#666;">${content.label}</p>` : ''}
            <p>${content.title || ''}</p>
            <p>${content.text || ''}</p>
            <p><a href="${content.linkUrl || '#'}" target="_blank">${content.linkUrl || ''}</a></p>
          </body>
        </html>
      `)
      previewWindow.document.close()
    },
    startEdit () {
      this.currentMode = 'edit'
    },
    cancelEditOrCreate () {
      if (!confirm('정말로 저장하지 않고 끝내시겠습니까?')) {
        return
      }
      if (this.mode === 'create') {
        this.$emit('close')
        return
      }
      this.form = JSON.parse(JSON.stringify(this.originalForm))
      this.errors = {}
      this.currentMode = 'detail'
    },
    handleClose () {
      this.$emit('close')
    }
  },
  data () {
    return {
      langs: ['ko', 'en', 'ja'],
      activeLang: 'ko',
      currentMode: this.mode,
      form: createDefaultForm(),
      originalForm: null,
      errors: {},
      sortSlots: [1, 2, 3]
    }
  }
}
</script>

<style lang="scss" scoped>
.banner-editor {
  .editor-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid $light;

    .title {
      font-size: 20px;
      font-weight: bold;
    }

    .actions {
      margin-left: auto;
    }
  }

  .editor-content {
    padding: 12px 16px 24px;
  }

  .section {
    margin-top: 12px;
    background: $white;
    border-radius: 8px;
    overflow: hidden;

    h5 {
      margin: 0;
      padding: 10px 12px;
      color: $white;
      background-color: $purple;
      font-size: 13px;
      font-weight: bold;
    }
  }

  .grid {
    padding: 8px 12px 4px;
  }

  .row {
    display: grid;
    grid-template-columns: 90px 1fr;
    align-items: start;
    column-gap: 12px;
    margin-bottom: 8px;

    label {
      padding-top: 8px;
      font-weight: 600;
    }

    .value {
      input,
      select {
        width: 100%;
        border: none;
        border-bottom: 2px solid $gray;
        background: transparent;
        padding: 8px 4px;
      }

      &.readonly {
        padding: 8px 4px;
        border-bottom: 2px solid $light;
      }
    }

    &.readonly-edit-row {
      label,
      .value.readonly {
        color: $gray;
      }
    }
  }

  .lang-tabs {
    display: flex;
    align-items: center;
    padding: 10px 12px 0;
    gap: 4px;

    button {
      border: 1px solid $light;
      background: $white;
      min-width: 42px;
      padding: 6px 8px;
      font-weight: bold;
      color: $gray;
      cursor: pointer;
    }

    button.active {
      background: $pink;
      border-color: $pink;
      color: $white;
    }

    .lang-guide {
      margin-left: 10px;
      color: $gray;
      font-size: 12px;
    }
  }

  .image-box {
    margin: 10px 12px;
    border: 1px dashed $gray;
    border-radius: 8px;
    min-height: 180px;
    padding: 12px;
    text-align: center;
    background: #fafafa;

    img {
      max-width: 100%;
      max-height: 300px;
      object-fit: contain;
      border-radius: 6px;
    }

    .placeholder {
      margin-top: 56px;
      color: $gray;
      line-height: 1.6;
      font-weight: 600;
    }

    .image-actions {
      margin-top: 12px;
    }
  }

  .error {
    margin-top: 4px;
    color: $pink;
    font-size: 12px;
  }

  .content-error {
    padding: 0 12px;
  }
}
</style>
